import type { AnimatableProperty, AnimationKeyframe, CanonicalEasing } from '@adflow/types';
import { isNamedEasing, namedEasingToBezier, type NamedEasing } from '@adflow/utils';

export const ANIMATION_PRESET_IDS = [
  'fade-in',
  'fade-out',
  'grow-in',
  'shrink-out',
  'slide-in-left',
  'slide-in-right',
  'slide-in-up',
  'slide-in-down',
  'slide-out-left',
  'slide-out-right',
  'slide-out-up',
  'slide-out-down',
] as const;

export type AnimationPresetId = (typeof ANIMATION_PRESET_IDS)[number];

export type AnimationPresetPhase = 'IN' | 'OUT';
export type AnimationPresetGroup = 'fade' | 'slide' | 'scale';

export type AnimationPresetParams = {
  duration: number;
  start: number;
  easing: NamedEasing;
  distance?: number;
};

export type AnimationPresetDefinition = {
  id: AnimationPresetId;
  label: string;
  phase: AnimationPresetPhase;
  group: AnimationPresetGroup;
  property: AnimatableProperty;
  defaultParams: AnimationPresetParams;
};

type KeyframeEndpoint = number | 'distance' | '-distance';

type PresetSpec = AnimationPresetDefinition & {
  from: KeyframeEndpoint;
  to: KeyframeEndpoint;
};

export const DEFAULT_IN_PARAMS: AnimationPresetParams = {
  duration: 0.5,
  start: 0,
  easing: 'ease-out',
};

export const DEFAULT_OUT_PARAMS: AnimationPresetParams = {
  duration: 0.5,
  start: 2,
  easing: 'ease-in',
};

export const DEFAULT_SLIDE_DISTANCE = 100;

const SLIDE_IN_PARAMS: AnimationPresetParams = {
  ...DEFAULT_IN_PARAMS,
  distance: DEFAULT_SLIDE_DISTANCE,
};

const SLIDE_OUT_PARAMS: AnimationPresetParams = {
  ...DEFAULT_OUT_PARAMS,
  distance: DEFAULT_SLIDE_DISTANCE,
};

const PRESET_SPECS: readonly PresetSpec[] = [
  {
    id: 'fade-in',
    label: 'Fade In',
    phase: 'IN',
    group: 'fade',
    property: 'opacity',
    defaultParams: DEFAULT_IN_PARAMS,
    from: 0,
    to: 1,
  },
  {
    id: 'fade-out',
    label: 'Fade Out',
    phase: 'OUT',
    group: 'fade',
    property: 'opacity',
    defaultParams: DEFAULT_OUT_PARAMS,
    from: 1,
    to: 0,
  },
  {
    id: 'grow-in',
    label: 'Grow In',
    phase: 'IN',
    group: 'scale',
    property: 'scale',
    defaultParams: DEFAULT_IN_PARAMS,
    from: 0,
    to: 1,
  },
  {
    id: 'shrink-out',
    label: 'Shrink Out',
    phase: 'OUT',
    group: 'scale',
    property: 'scale',
    defaultParams: DEFAULT_OUT_PARAMS,
    from: 1,
    to: 0,
  },
  {
    id: 'slide-in-left',
    label: 'Slide In Left',
    phase: 'IN',
    group: 'slide',
    property: 'x',
    defaultParams: SLIDE_IN_PARAMS,
    from: '-distance',
    to: 0,
  },
  {
    id: 'slide-in-right',
    label: 'Slide In Right',
    phase: 'IN',
    group: 'slide',
    property: 'x',
    defaultParams: SLIDE_IN_PARAMS,
    from: 'distance',
    to: 0,
  },
  {
    id: 'slide-in-up',
    label: 'Slide In Up',
    phase: 'IN',
    group: 'slide',
    property: 'y',
    defaultParams: SLIDE_IN_PARAMS,
    from: '-distance',
    to: 0,
  },
  {
    id: 'slide-in-down',
    label: 'Slide In Down',
    phase: 'IN',
    group: 'slide',
    property: 'y',
    defaultParams: SLIDE_IN_PARAMS,
    from: 'distance',
    to: 0,
  },
  {
    id: 'slide-out-left',
    label: 'Slide Out Left',
    phase: 'OUT',
    group: 'slide',
    property: 'x',
    defaultParams: SLIDE_OUT_PARAMS,
    from: 0,
    to: '-distance',
  },
  {
    id: 'slide-out-right',
    label: 'Slide Out Right',
    phase: 'OUT',
    group: 'slide',
    property: 'x',
    defaultParams: SLIDE_OUT_PARAMS,
    from: 0,
    to: 'distance',
  },
  {
    id: 'slide-out-up',
    label: 'Slide Out Up',
    phase: 'OUT',
    group: 'slide',
    property: 'y',
    defaultParams: SLIDE_OUT_PARAMS,
    from: 0,
    to: '-distance',
  },
  {
    id: 'slide-out-down',
    label: 'Slide Out Down',
    phase: 'OUT',
    group: 'slide',
    property: 'y',
    defaultParams: SLIDE_OUT_PARAMS,
    from: 0,
    to: 'distance',
  },
];

export const ANIMATION_PRESETS: Record<AnimationPresetId, AnimationPresetDefinition> =
  PRESET_SPECS.reduce((acc, spec) => {
    acc[spec.id] = {
      id: spec.id,
      label: spec.label,
      phase: spec.phase,
      group: spec.group,
      property: spec.property,
      defaultParams: spec.defaultParams,
    };
    return acc;
  }, {} as Record<AnimationPresetId, AnimationPresetDefinition>);

const PRESET_SPEC_BY_ID = PRESET_SPECS.reduce((acc, spec) => {
  acc[spec.id] = spec;
  return acc;
}, {} as Record<AnimationPresetId, PresetSpec>);

export function isAnimationPresetId(value: string): value is AnimationPresetId {
  return Object.hasOwn(ANIMATION_PRESETS, value);
}

export function getAnimationPreset(presetId: string): AnimationPresetDefinition {
  if (!isAnimationPresetId(presetId)) {
    throw new Error(`Unknown animation preset "${presetId}"`);
  }
  return ANIMATION_PRESETS[presetId];
}

export function listAnimationPresets(phase?: AnimationPresetPhase): AnimationPresetDefinition[] {
  const presets = ANIMATION_PRESET_IDS.map((id) => ANIMATION_PRESETS[id]);
  return phase ? presets.filter((preset) => preset.phase === phase) : presets;
}

function resolveEndpoint(endpoint: KeyframeEndpoint, distance: number): number {
  if (endpoint === 'distance') {
    return distance;
  }
  if (endpoint === '-distance') {
    return -distance;
  }
  return endpoint;
}

function readNumber(
  params: Record<string, number | string | boolean> | null | undefined,
  key: string,
  fallback: number,
): number {
  const value = params?.[key];
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function readEasing(
  params: Record<string, number | string | boolean> | null | undefined,
  fallback: NamedEasing,
): NamedEasing {
  const value = params?.easing;
  return typeof value === 'string' && isNamedEasing(value) ? value : fallback;
}

export function resolvePresetParams(
  preset: AnimationPresetDefinition,
  params?: Record<string, number | string | boolean> | null,
): AnimationPresetParams {
  const defaults = preset.defaultParams;
  const resolved: AnimationPresetParams = {
    duration: Math.max(0, readNumber(params, 'duration', defaults.duration)),
    start: readNumber(params, 'start', defaults.start),
    easing: readEasing(params, defaults.easing),
  };
  if (preset.group === 'slide') {
    resolved.distance = Math.max(
      0,
      readNumber(params, 'distance', defaults.distance ?? DEFAULT_SLIDE_DISTANCE),
    );
  }
  return resolved;
}

export function toStoredPresetParams(
  preset: AnimationPresetDefinition,
  resolved: AnimationPresetParams,
): Record<string, number | string | boolean> {
  const stored: Record<string, number | string | boolean> = {
    duration: resolved.duration,
    start: resolved.start,
    easing: resolved.easing,
  };
  if (preset.group === 'slide') {
    stored.distance = resolved.distance ?? DEFAULT_SLIDE_DISTANCE;
  }
  return stored;
}

export function generatePresetKeyframes(
  presetId: string,
  params: AnimationPresetParams,
  effectId: string,
): AnimationKeyframe[] {
  if (!isAnimationPresetId(presetId)) {
    throw new Error(`Unknown animation preset "${presetId}"`);
  }
  const spec = PRESET_SPEC_BY_ID[presetId];
  const easing: CanonicalEasing = namedEasingToBezier(params.easing);
  const distance = params.distance ?? DEFAULT_SLIDE_DISTANCE;
  const from = resolveEndpoint(spec.from, distance);
  const to = resolveEndpoint(spec.to, distance);
  return [
    { t: params.start, value: from, easing, effectId },
    { t: params.start + params.duration, value: to, easing, effectId },
  ];
}
